import React, { useEffect, useState } from "react";
import { UserCog, Plus } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Loader } from "@/components/dashboard/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ALL_ROLES } from "@/data/constants";
import { Trash2, Power } from "lucide-react";
import { useSelect, SelectCheck, SelectionToolbar } from "@/components/admin/BulkBar";
import { trashAction } from "@/lib/trash";
import { AdminSetPasswordDialog } from "@/components/PasswordDialogs";
import { KeyRound } from "lucide-react";

const emptyForm = { first_name: "", last_name: "", email: "", phone: "", password: "Delya2026!", roles: ["secretary"], can_take_missions: true, public_visible: false, job_title: "", bio: "" };

export default function AdminTeam() {
  const { hasPerm, hasRole } = useAuth();
  const [team, setTeam] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [f, setF] = useState(emptyForm);
  const [arch, setArch] = useState(false);
  const sel = useSelect();
  const canManageRoles = hasPerm("manage_roles");
  const isSuper = hasRole("super_admin");

  const load = () => api.get(`/users?include_archived=${arch}`).then(({ data }) => setTeam((data || []).filter((u) => (u.roles || []).some((r) => r !== "doctor"))));
  useEffect(() => { sel.clear(); load(); /* eslint-disable-next-line */ }, [arch]);

  const openNew = () => { setEditing(null); setF(emptyForm); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setF({ ...emptyForm, ...u, password: "" }); setOpen(true); };
  const toggleRole = (r) => setF((s) => ({ ...s, roles: s.roles.includes(r) ? s.roles.filter((x) => x !== r) : [...s.roles, r] }));

  const save = async () => {
    try {
      if (editing) { await api.put(`/users/${editing.user_id}`, f); toast.success("Membre mis à jour"); }
      else { await api.post("/users", f); toast.success("Membre créé"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleActive = async () => {
    try { await api.put(`/users/${editing.user_id}`, { active: !editing.active }); toast.success(editing.active ? "Compte désactivé" : "Compte activé"); setOpen(false); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delMember = async () => {
    if (!window.confirm("Déplacer ce collaborateur vers la corbeille ? Son historique de missions est préservé.")) return;
    try { await trashAction("users", [editing.user_id], "delete"); toast.success("Collaborateur déplacé vers la corbeille"); setOpen(false); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (team === null) return <Loader />;
  return (
    <div>
      <PageHeader title="Équipe" subtitle="Gérez les membres, leurs rôles et permissions."
        action={<Button className="bg-primary" onClick={openNew} data-testid="add-member-btn"><Plus className="h-4 w-4 mr-1.5" />Ajouter un membre</Button>} />
      <SelectionToolbar items={team} idKey="user_id" sel={sel} entity="users" onDone={load} label="collaborateur" archived={arch} onToggleArchived={() => setArch((a) => !a)} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{team.map((u) => (
        <div key={u.user_id} role="button" tabIndex={0} onClick={() => openEdit(u)} className={`relative bg-white rounded-xl border p-5 pl-11 text-left hover:shadow-md transition-all cursor-pointer ${sel.has(u.user_id) ? "border-primary ring-1 ring-primary/30" : "border-slate-200"}`} data-testid={`member-${u.user_id}`}>
          <SelectCheck className="absolute left-3 top-5" checked={sel.has(u.user_id)} onChange={() => sel.toggle(u.user_id)} testId={`select-member-${u.user_id}`} />
          <div className="flex items-center gap-3">
            <Avatar className="h-11 w-11"><AvatarFallback className="bg-primary/10 text-primary font-semibold">{(u.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
            <div className="min-w-0"><p className="font-semibold text-slate-900 truncate">{u.name}</p><p className="text-sm text-slate-500 truncate">{u.job_title || u.email}</p></div>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {(u.roles || []).map((r) => <span key={r} className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">{ALL_ROLES.find((x) => x.key === r)?.label || r}</span>)}
          </div>
          <div className="mt-2 flex gap-3 text-xs text-slate-400">
            <span className={u.active ? "" : "text-rose-500 font-medium"}>{u.active ? "Actif" : "Inactif"}</span>{u.can_take_missions && <span>· Missions ✓</span>}{u.public_visible && <span>· Public ✓</span>}
          </div>
        </div>))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto custom-scroll">
          <DialogHeader><DialogTitle>{editing ? "Modifier le membre" : "Ajouter un membre"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Prénom</Label><Input value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} className="mt-1" data-testid="member-firstname" /></div>
              <div><Label>Nom</Label><Input value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} className="mt-1" data-testid="member-lastname" /></div>
            </div>
            {!editing && <div><Label>E-mail</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1" data-testid="member-email" /></div>}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Téléphone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1" /></div>
              <div><Label>Fonction (publique)</Label><Input value={f.job_title} onChange={(e) => setF({ ...f, job_title: e.target.value })} className="mt-1" /></div>
            </div>
            {!editing && <div><Label>Mot de passe initial</Label><Input value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="mt-1" /></div>}
            <div>
              <Label>Rôles</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {ALL_ROLES.map((r) => {
                  const disabled = r.key === "super_admin" && !canManageRoles;
                  return (
                    <label key={r.key} className={`flex items-center gap-2 text-sm ${disabled ? "opacity-40" : "cursor-pointer"}`}>
                      <Checkbox checked={f.roles.includes(r.key)} disabled={disabled} onCheckedChange={() => !disabled && toggleRole(r.key)} data-testid={`role-${r.key}`} />{r.label}
                    </label>
                  );
                })}
              </div>
            </div>
            <div><Label>Biographie (publique)</Label><Textarea rows={2} value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} className="mt-1" /></div>
            <div className="space-y-3 pt-2 border-t border-slate-100">
              <label className="flex items-center justify-between"><span className="text-sm">Peut prendre des missions</span><Switch checked={f.can_take_missions} onCheckedChange={(v) => setF({ ...f, can_take_missions: v })} data-testid="member-can-missions" /></label>
              <label className="flex items-center justify-between"><span className="text-sm">Visible sur la page équipe publique</span><Switch checked={f.public_visible} onCheckedChange={(v) => setF({ ...f, public_visible: v })} data-testid="member-public" /></label>
              <label className="flex items-center justify-between"><span className="text-sm">Compte actif</span><Switch checked={f.active !== false} onCheckedChange={(v) => setF({ ...f, active: v })} /></label>
            </div>
          </div>
          <DialogFooter>
            {editing && (
              <div className="flex flex-wrap gap-2 mr-auto">
                <Button variant="outline" onClick={toggleActive} data-testid="member-toggle-active"><Power className="h-4 w-4 mr-1.5" />{editing.active ? "Désactiver" : "Activer"}</Button>
                {isSuper && <Button variant="outline" onClick={() => setPwOpen(true)} data-testid="member-reset-password"><KeyRound className="h-4 w-4 mr-1.5" />Mot de passe</Button>}
                <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={delMember} data-testid="member-delete"><Trash2 className="h-4 w-4 mr-1.5" />Supprimer</Button>
              </div>
            )}
            <Button className="bg-primary" onClick={save} data-testid="member-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {editing && <AdminSetPasswordDialog open={pwOpen} onOpenChange={setPwOpen} userId={editing.user_id} userName={editing.name} />}
    </div>
  );
}
